import { memo, useMemo } from "react";

import { RVFlowBlockVariants } from "Components/elements/RVFlow/constants";
import { BaseFlowNodeContainer } from "Components/elements/RVFlow/Shared/FlowNodes/Shared/BaseFlowNodeContainer/BaseFlowNodeContainer";
import { getJourneyDynamicHandles } from "Components/elements/RVFlow/Shared/JourneyNodePresentation";
import { NodeType } from "Types/Journey";

import { AgentlessCallBlock } from "../../../shared/Blocks/_DemoBlocks/AgentlessCallBlock/AgentlessCallBlock";
import { CustomAITaskBlock } from "../../../shared/Blocks/_DemoBlocks/CustomAITaskBlock/CustomAITaskBlock";
import { DecisionAIBlock } from "../../../shared/Blocks/_DemoBlocks/DecisionAIBlock/DecisionAIBlock";
import { EightByEightBlock } from "../../../shared/Blocks/_DemoBlocks/EightByEightBlock/EightByEightBlock";
import { EmailTaskBlock } from "../../../shared/Blocks/_DemoBlocks/EmailTaskBlock/EmailTaskBlock";
import { Five9Block } from "../../../shared/Blocks/_DemoBlocks/Five9Block/Five9Block";
import { GenesysBlock } from "../../../shared/Blocks/_DemoBlocks/GenesysBlock/GenesysBlock";
import { NiceBlock } from "../../../shared/Blocks/_DemoBlocks/NiceBlock/NiceBlock";
import { RinglessVMBlock } from "../../../shared/Blocks/_DemoBlocks/RinglessVMBlock/RinglessVMBlock";
import { SendEmailBlock } from "../../../shared/Blocks/_DemoBlocks/SendEmailBlock/SendEmailBlock";
import { SMSAIBlock } from "../../../shared/Blocks/_DemoBlocks/SMSAIBlock/SMSAIBlock";
import { TalkdeskBlock } from "../../../shared/Blocks/_DemoBlocks/TalkdeskBlock/TalkdeskBlock";
import { VoiceAIBlock } from "../../../shared/Blocks/_DemoBlocks/VoiceAIBlock/VoiceAIBlock";
import { ABTestBlock } from "../../../shared/Blocks/ABTestBlock/ABTestBlock";
import { ActionBlock } from "../../../shared/Blocks/ActionBlock/ActionBlock";
import { AssignTargetAgentActionBlock } from "../../../shared/Blocks/AssignTargetAgentBlock/AssignTargetAgentBlock";
import { CancelActionBlock } from "../../../shared/Blocks/CancelActionBlock/CancelActionBlock";
import { ConditionalBlock } from "../../../shared/Blocks/ConditionalBlock/ConditionalBlock";
import { CustomTaskBlock } from "../../../shared/Blocks/CustomTaskBlock/CustomTaskBlock";
import { DelayBlock } from "../../../shared/Blocks/DelayBlock/DelayBlock";
import { ReminderBlock } from "../../../shared/Blocks/ReminderBlock/ReminderBlock";
import { ScheduledCallbackActionBlock } from "../../../shared/Blocks/ScheduledCallbackActionBlock/ScheduledCallbackActionBlock";
import { StickyNoteBlock } from "../../../shared/Blocks/StickyNoteBlock/StickyNoteBlock";
import { TriggerBlock } from "../../../shared/Blocks/TriggerBlock/TriggerBlock";
import { UpdateContactBlock } from "../../../shared/Blocks/UpdateContactBlock/UpdateContactBlock";
import { UpdateTaskActionBlock } from "../../../shared/Blocks/UpdateTaskActionBlock/UpdateTaskActionBlock";
import { WebhookActionBlock } from "../../../shared/Blocks/WebhookActionBlock/WebhookActionBlock";
import {
    JounreyInputNode,
    JourneyInputOutputNode,
    JourneyBranchNode,
    JourneyDynamicPercentageBranchNode,
    JourneyDynamicBranchNode,
} from "./Components/SharedJourneyNodes";

export const StickyNoteNode = memo(function StickyNoteNode({
    id,
    selected,
    data,
    xPos,
    yPos,
}: {
    id: string;
    selected: boolean;
    data: any;
    xPos: number;
    yPos: number;
}) {
    return (
        <BaseFlowNodeContainer isCircleNode selected={selected} connectingNodesFrom={false}>
            <StickyNoteBlock id={id} data={data} xPos={xPos} yPos={yPos} />
        </BaseFlowNodeContainer>
    );
});

export const TriggerNode = memo(function TriggerNode({
    data,
    isConnectable,
    selected,
}: {
    data: any;
    isConnectable: boolean;
    selected: boolean;
}): JSX.Element {
    return (
        <JounreyInputNode isConnectable={isConnectable} selected={selected}>
            <TriggerBlock data={data} />
        </JounreyInputNode>
    );
});

export const DelayNode = memo(function DelayNode({
    id,
    data,
    isConnectable,
    selected,
}: {
    id: string;
    data: any;
    isConnectable: boolean;
    selected: boolean;
}): JSX.Element {
    return (
        <JourneyInputOutputNode id={id} isConnectable={isConnectable} selected={selected}>
            <DelayBlock data={{ nodeId: id, ...data }} />
        </JourneyInputOutputNode>
    );
});

export const CallNode = memo(function CallNode({
    id,
    data,
    isConnectable,
    selected,
}: {
    id: string;
    data: any;
    isConnectable: boolean;
    selected: boolean;
}): JSX.Element {
    return (
        <JourneyInputOutputNode id={id} isConnectable={isConnectable} selected={selected}>
            <ActionBlock data={data} type="Create Call Task" />
        </JourneyInputOutputNode>
    );
});

export const SmsNode = memo(function SmsNode({
    id,
    data,
    isConnectable,
    selected,
}: {
    id: string;
    data: any;
    isConnectable: boolean;
    selected: boolean;
}): JSX.Element {
    return (
        <JourneyInputOutputNode id={id} isConnectable={isConnectable} selected={selected}>
            <ActionBlock data={data} type="Send SMS" />
        </JourneyInputOutputNode>
    );
});

export const AgentSMSTaskNode = memo(function AgentSMSTaskNode({
    id,
    data,
    isConnectable,
    selected,
}: {
    id: string;
    data: any;
    isConnectable: boolean;
    selected: boolean;
}): JSX.Element {
    return (
        <JourneyInputOutputNode id={id} isConnectable={isConnectable} selected={selected}>
            <ActionBlock data={data} type="Create SMS Task" />
        </JourneyInputOutputNode>
    );
});

export const CustomTaskNode = memo(function CustomTaskNode({
    id,
    data,
    isConnectable,
    selected,
}: {
    id: string;
    data: any;
    isConnectable: boolean;
    selected: boolean;
}): JSX.Element {
    return (
        <JourneyInputOutputNode id={id} isConnectable={isConnectable} selected={selected}>
            <CustomTaskBlock data={data} />
        </JourneyInputOutputNode>
    );
});

export const CancelTaskNode = memo(function CancelTaskNode({
    id,
    data,
    isConnectable,
    selected,
}: {
    id: string;
    data: any;
    isConnectable: boolean;
    selected: boolean;
}): JSX.Element {
    return (
        <JourneyInputOutputNode id={id} isConnectable={isConnectable} selected={selected}>
            <CancelActionBlock data={data} />
        </JourneyInputOutputNode>
    );
});

export const WebhookTaskNode = memo(function WebhookTaskNode({
    id,
    data,
    isConnectable,
    selected,
}: {
    id: string;
    data: any;
    isConnectable: boolean;
    selected: boolean;
}): JSX.Element {
    return (
        <JourneyInputOutputNode id={id} isConnectable={isConnectable} selected={selected}>
            <WebhookActionBlock data={data} />
        </JourneyInputOutputNode>
    );
});

export const AssignTargetAgentNode = memo(function AssignTargetAgentNode({
    id,
    data,
    isConnectable,
    selected,
}: {
    id: string;
    data: any;
    isConnectable: boolean;
    selected: boolean;
}): JSX.Element {
    return (
        <JourneyInputOutputNode id={id} isConnectable={isConnectable} selected={selected}>
            <AssignTargetAgentActionBlock data={data} />
        </JourneyInputOutputNode>
    );
});

export const UpdateTaskNode = memo(function UpdateTaskNode({
    id,
    data,
    isConnectable,
    selected,
}: {
    id: string;
    data: any;
    isConnectable: boolean;
    selected: boolean;
}): JSX.Element {
    return (
        <JourneyInputOutputNode id={id} isConnectable={isConnectable} selected={selected}>
            <UpdateTaskActionBlock data={data} />
        </JourneyInputOutputNode>
    );
});

export const UpdateContactNode = memo(function UpdateContactNode({
    id,
    data,
    isConnectable,
    selected,
}: {
    id: string;
    data: any;
    isConnectable: boolean;
    selected: boolean;
}): JSX.Element {
    return (
        <JourneyInputOutputNode id={id} isConnectable={isConnectable} selected={selected}>
            <UpdateContactBlock variant={RVFlowBlockVariants.node} data={data} />
        </JourneyInputOutputNode>
    );
});

export const ScheduledCallbackNode = memo(function ScheduledCallbackNode({
    id,
    data,
    isConnectable,
    selected,
}: {
    id: string;
    data: any;
    isConnectable: boolean;
    selected: boolean;
}): JSX.Element {
    return (
        <JourneyInputOutputNode id={id} isConnectable={isConnectable} selected={selected}>
            <ScheduledCallbackActionBlock data={data} />
        </JourneyInputOutputNode>
    );
});

export const ConditionalNode = memo(function ConditionalNode({
    id,
    data,
    isConnectable,
    selected,
}: {
    id: string;
    data: any;
    isConnectable: boolean;
    selected: boolean;
}): JSX.Element {
    return (
        <JourneyBranchNode id={id} isConnectable={isConnectable} selected={selected}>
            <ConditionalBlock data={data} />
        </JourneyBranchNode>
    );
});

export const ABTestNode = memo(function ABTestNode({
    id,
    data,
    isConnectable,
}: {
    id: string;
    data: any;
    isConnectable: boolean;
}): JSX.Element {
    const handles = getJourneyDynamicHandles(NodeType.ABTest, data);

    return (
        <JourneyDynamicPercentageBranchNode id={id} isConnectable={isConnectable} handles={handles}>
            <ABTestBlock data={data} />
        </JourneyDynamicPercentageBranchNode>
    );
});

export const ReminderNode = memo(function ReminderNode({
    id,
    data,
    isConnectable,
    selected,
}: {
    id: string;
    data: any;
    isConnectable: boolean;
    selected: boolean;
}): JSX.Element {
    return (
        <JourneyInputOutputNode id={id} isConnectable={isConnectable} selected={selected}>
            <ReminderBlock data={data} />
        </JourneyInputOutputNode>
    );
});

// DEMO NODES

export const EightByEightNode = memo(function EightByEightNode({
    id,
    data,
    isConnectable,
    selected,
}: {
    id: string;
    data: any;
    isConnectable: boolean;
    selected: boolean;
}): JSX.Element {
    return (
        <JourneyInputOutputNode id={id} isConnectable={isConnectable} selected={selected}>
            <EightByEightBlock variant={RVFlowBlockVariants.node} data={data} />
        </JourneyInputOutputNode>
    );
});

export const RinglessVMNode = memo(function RinglessVMNode({
    id,
    data,
    isConnectable,
    selected,
}: {
    id: string;
    data: any;
    isConnectable: boolean;
    selected: boolean;
}): JSX.Element {
    return (
        <JourneyInputOutputNode id={id} isConnectable={isConnectable} selected={selected}>
            <RinglessVMBlock variant={RVFlowBlockVariants.node} data={data} />
        </JourneyInputOutputNode>
    );
});

export const AgentlessCallNode = memo(function AgentlessCallNode({
    id,
    data,
    isConnectable,
    selected,
}: {
    id: string;
    data: any;
    isConnectable: boolean;
    selected: boolean;
}): JSX.Element {
    return (
        <JourneyInputOutputNode id={id} isConnectable={isConnectable} selected={selected}>
            <AgentlessCallBlock variant={RVFlowBlockVariants.node} data={data} />
        </JourneyInputOutputNode>
    );
});

export const VoiceAINode = memo(function VoiceAINode({
    id,
    data,
    isConnectable,
    selected,
}: {
    id: string;
    data: any;
    isConnectable: boolean;
    selected: boolean;
}): JSX.Element {
    return (
        <JourneyInputOutputNode id={id} isConnectable={isConnectable} selected={selected}>
            <VoiceAIBlock variant={RVFlowBlockVariants.node} data={data} />
        </JourneyInputOutputNode>
    );
});

export const GenesysNode = memo(function GenesysNode({
    id,
    data,
    isConnectable,
    selected,
}: {
    id: string;
    data: any;
    isConnectable: boolean;
    selected: boolean;
}): JSX.Element {
    return (
        <JourneyInputOutputNode id={id} isConnectable={isConnectable} selected={selected}>
            <GenesysBlock variant={RVFlowBlockVariants.node} data={data} />
        </JourneyInputOutputNode>
    );
});

export const TalkdeskNode = memo(function TalkdeskNode({
    id,
    data,
    isConnectable,
    selected,
}: {
    id: string;
    data: any;
    isConnectable: boolean;
    selected: boolean;
}): JSX.Element {
    return (
        <JourneyInputOutputNode id={id} isConnectable={isConnectable} selected={selected}>
            <TalkdeskBlock variant={RVFlowBlockVariants.node} data={data} />
        </JourneyInputOutputNode>
    );
});

export const NiceNode = memo(function NiceNode({
    id,
    data,
    isConnectable,
    selected,
}: {
    id: string;
    data: any;
    isConnectable: boolean;
    selected: boolean;
}): JSX.Element {
    return (
        <JourneyInputOutputNode id={id} isConnectable={isConnectable} selected={selected}>
            <NiceBlock variant={RVFlowBlockVariants.node} data={data} />
        </JourneyInputOutputNode>
    );
});

export const Five9Node = memo(function Five9Node({
    id,
    data,
    isConnectable,
    selected,
}: {
    id: string;
    data: any;
    isConnectable: boolean;
    selected: boolean;
}): JSX.Element {
    return (
        <JourneyInputOutputNode id={id} isConnectable={isConnectable} selected={selected}>
            <Five9Block variant={RVFlowBlockVariants.node} data={data} />
        </JourneyInputOutputNode>
    );
});

export const SMSAINode = memo(function SMSAINode({
    id,
    data,
    isConnectable,
    selected,
}: {
    id: string;
    data: any;
    isConnectable: boolean;
    selected: boolean;
}): JSX.Element {
    return (
        <JourneyInputOutputNode id={id} isConnectable={isConnectable} selected={selected}>
            <SMSAIBlock variant={RVFlowBlockVariants.node} data={data} />
        </JourneyInputOutputNode>
    );
});

export const CustomAITaskNode = memo(function CustomAITaskNode({
    id,
    data,
    isConnectable,
    selected,
}: {
    id: string;
    data: any;
    isConnectable: boolean;
    selected: boolean;
}): JSX.Element {
    return (
        <JourneyInputOutputNode id={id} isConnectable={isConnectable} selected={selected}>
            <CustomAITaskBlock variant={RVFlowBlockVariants.node} data={data} />
        </JourneyInputOutputNode>
    );
});

export const DecisionAINode = memo(function DecisionAINode({
    id,
    data,
    isConnectable,
}: {
    id: string;
    data: any;
    isConnectable: boolean;
}): JSX.Element {
    const handles = useMemo(() => getJourneyDynamicHandles(NodeType.DecisionAI, data), [data]);

    return (
        <JourneyDynamicBranchNode id={id} isConnectable={isConnectable} handles={handles}>
            <DecisionAIBlock variant={RVFlowBlockVariants.node} data={data} />
        </JourneyDynamicBranchNode>
    );
});

export const SendEmailNode = memo(function SendEmailNode({
    id,
    data,
    isConnectable,
    selected,
}: {
    id: string;
    data: any;
    isConnectable: boolean;
    selected: boolean;
}): JSX.Element {
    return (
        <JourneyInputOutputNode id={id} isConnectable={isConnectable} selected={selected}>
            <SendEmailBlock variant={RVFlowBlockVariants.node} data={data} />
        </JourneyInputOutputNode>
    );
});

export const EmailTaskNode = memo(function EmailTaskNode({
    id,
    data,
    isConnectable,
    selected,
}: {
    id: string;
    data: any;
    isConnectable: boolean;
    selected: boolean;
}): JSX.Element {
    return (
        <JourneyInputOutputNode id={id} isConnectable={isConnectable} selected={selected}>
            <EmailTaskBlock variant={RVFlowBlockVariants.node} data={data} />
        </JourneyInputOutputNode>
    );
});
